/** Independent anonymous contract: deliberately outside the BOSS request chain. */
import {
  type DocumentPreview,
  type DocumentPreviewOptions,
  parseDocumentPreview,
} from './documentPreview';
export const ASSET_SHARE_RESOLVE_PATH =
  '/openApi/conference/core/assetShare/v1/resolve';
export type ShareAction = 'INFO' | 'PREVIEW' | 'DOWNLOAD';
export interface PublicFileAccess {
  url: string;
  expiresAt: string;
  serverTime: string;
  disposition: string;
}
export interface PublicShare {
  name: string;
  resourceType: 'IMAGE' | 'AUDIO' | 'VIDEO' | 'DOCUMENT';
  mimeType: string;
  size: number;
  shareExpiresAt: string;
  serverTime: string;
  allowedActions: ShareAction[];
  fileAccess?: PublicFileAccess;
  previewDocument?: DocumentPreview;
}
export const isAssetSharePath = (pathname: string) =>
  /^\/asset-share\/[A-Za-z0-9_-]+\/?$/.test(pathname);
export const readShareCredential = (
  location: Pick<Location, 'pathname' | 'hash'>,
) => {
  if (!isAssetSharePath(location.pathname)) return undefined;
  const shareId = location.pathname.split('/')[2];
  const token = new URLSearchParams(location.hash.slice(1)).get('token');
  if (!token || !/^[A-Za-z0-9_-]{43,256}$/.test(token)) return undefined;
  return { shareId, token };
};
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;
const timestamp = (value: unknown): value is string =>
  typeof value === 'string' && Number.isFinite(Date.parse(value));
const safeUrl = (value: unknown): value is string => {
  try {
    return typeof value === 'string' && new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};
export class ShareUnavailableError extends Error {
  constructor(readonly rateLimited = false) {
    super(rateLimited ? '访问频繁，请稍后重试' : '分享不可用或已到期');
  }
}
const previewErrors: Record<string, string> = {
  ASSET_PREVIEW_UNSUPPORTED: '此文件格式暂不支持预览',
  ASSET_PREVIEW_TOO_COMPLEX: '文件内容过于复杂，超出预览范围',
  ASSET_PREVIEW_ENCRYPTED: '加密文件暂不支持预览',
  ASSET_PREVIEW_FAILED: '文件无法预览，请检查文件或稍后重试',
  ASSET_PREVIEW_BUSY: '预览繁忙，请稍后重试',
  ASSET_PREVIEW_TIMEOUT: '预览处理超时，请稍后重试',
};
export const parsePublicShare = (
  data: unknown,
  action: ShareAction,
): PublicShare => {
  if (
    !isRecord(data) ||
    typeof data.name !== 'string' ||
    (data.resourceType !== 'IMAGE' &&
      data.resourceType !== 'AUDIO' &&
      data.resourceType !== 'VIDEO' &&
      data.resourceType !== 'DOCUMENT') ||
    typeof data.mimeType !== 'string' ||
    typeof data.size !== 'number' ||
    !timestamp(data.shareExpiresAt) ||
    !timestamp(data.serverTime) ||
    !Array.isArray(data.allowedActions)
  ) throw new ShareUnavailableError();
  const allowedActions = data.allowedActions.filter(
    (item): item is ShareAction =>
      item === 'INFO' || item === 'PREVIEW' || item === 'DOWNLOAD',
  );
  let fileAccess: PublicFileAccess | undefined;
  let previewDocument: DocumentPreview | undefined;
  if (action !== 'INFO' && !allowedActions.includes(action)) throw new ShareUnavailableError();
  if (action === 'PREVIEW' && data.previewDocument != null) {
    if (data.fileAccess != null) throw new ShareUnavailableError();
    try {
      previewDocument = parseDocumentPreview(
        data.previewDocument,
        data.shareExpiresAt,
      );
    } catch {
      throw new ShareUnavailableError();
    }
  } else if (action !== 'INFO') {
    const access = data.fileAccess;
    if (
      !isRecord(access) ||
      !safeUrl(access.url) ||
      !timestamp(access.expiresAt) ||
      !timestamp(access.serverTime) ||
      typeof access.disposition !== 'string' ||
      Date.parse(access.expiresAt) > Date.parse(data.shareExpiresAt) ||
      Date.parse(access.expiresAt) - Date.parse(access.serverTime) > 60000
    ) throw new ShareUnavailableError();
    fileAccess = {
      url: access.url,
      expiresAt: access.expiresAt,
      serverTime: access.serverTime,
      disposition: access.disposition,
    };
  }
  return {
    name: data.name,
    resourceType: data.resourceType,
    mimeType: data.mimeType,
    size: data.size,
    shareExpiresAt: data.shareExpiresAt,
    serverTime: data.serverTime,
    allowedActions,
    fileAccess,
    previewDocument,
  };
};
export const resolvePublicShare = async (
  credential: { shareId: string; token: string },
  action: ShareAction,
  signal?: AbortSignal,
  previewOptions?: DocumentPreviewOptions,
): Promise<PublicShare> => {
  const response = await fetch(ASSET_SHARE_RESOLVE_PATH, {
    method: 'POST',
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...credential,
      action,
      ...(action === 'PREVIEW' && previewOptions ? { previewOptions } : {}),
    }),
    signal,
  });
  if (!response.ok) throw new ShareUnavailableError(response.status === 429);
  const body: unknown = await response.json();
  if (!isRecord(body) || body.success !== true) {
    const code = isRecord(body)
      ? (body.errorCode ?? body.uniformErrorCode)
      : undefined;
    if (
      action === 'PREVIEW' &&
      typeof code === 'string' &&
      Object.hasOwn(previewErrors, code)
    ) throw new Error(previewErrors[code]);
    throw new ShareUnavailableError(
      isRecord(body) &&
        [body.errorCode, body.uniformErrorCode].some(
          (codeValue) =>
            codeValue === 'RATE_LIMITED' || codeValue === 'ASSET_RATE_LIMITED',
        ),
    );
  }
  return parsePublicShare(body.data, action);
};
